import { Eye, Loader2, RefreshCcw, Star, Trash2 } from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { canManageService, ServiceDomain } from "src/lib/roles";
import { selectUserProfile } from "src/store/authSlice";
import type { CarouselPublicationListItem } from "src/types/carouselPublication";

interface PastCarouselCardProps {
  publication: CarouselPublicationListItem;
  busy: boolean;
  onPreview: (pub: CarouselPublicationListItem) => void;
  onRepublish: (pub: CarouselPublicationListItem) => void;
  onToggleStar: (pub: CarouselPublicationListItem) => void;
  onDelete: (pub: CarouselPublicationListItem) => void;
}

const MAX_THUMBS = 5;

function formatPublishedAt(ts: { toMillis(): number }): string {
  const d = new Date(ts.toMillis());
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRelative(ts: { toMillis(): number }): string {
  const diff = Date.now() - ts.toMillis();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

export const PastCarouselCard: React.FC<PastCarouselCardProps> = ({
  publication,
  busy,
  onPreview,
  onRepublish,
  onToggleStar,
  onDelete,
}) => {
  const isStarred = Boolean(publication.starredAt);
  const thumbs = publication.cards.slice(0, MAX_THUMBS);
  const overflow = publication.cardCount - thumbs.length;
  const userProfile = useSelector(selectUserProfile);

  const canManage =
    !!userProfile &&
    canManageService(userProfile.role, ServiceDomain.HappeningNow);
  return (
    <li
      className={[
        "overflow-hidden rounded-2xl border-4 bg-white shadow-[0_4px_0_#D0D0D0]",
        isStarred ? "border-[#F59E0B]" : "border-[#E5E5E5]",
      ].join(" ")}
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-extrabold text-[#3C3C3C]">
              {formatPublishedAt(publication.publishedAt)}
            </span>
            <span className="rounded-md bg-[#F1F5F9] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#475569]">
              {formatRelative(publication.publishedAt)}
            </span>
            <span className="rounded-md bg-[#EEF2FF] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#4338CA]">
              {publication.cardCount}{" "}
              {publication.cardCount === 1 ? "card" : "cards"}
            </span>
            {isStarred && (
              <span className="inline-flex items-center gap-1 rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-[#92400E]">
                <Star className="h-3 w-3 fill-current" aria-hidden />
                Starred
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-xs font-bold text-[#777777]">
            By{" "}
            {publication.publishedByDisplayName?.trim() ||
              publication.publishedBy}
          </p>
          <div className="mt-2 flex items-center -space-x-2">
            {thumbs.map((card) => (
              <div
                key={card.id}
                className="h-9 w-9 overflow-hidden rounded-full border-2 border-white bg-[#F7F7F7]"
                title={card.title}
              >
                <img
                  src={card.imageUrl}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
            {overflow > 0 && (
              <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#3C3C3C] text-[10px] font-extrabold text-white">
                +{overflow}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 sm:flex-nowrap">
          <button
            type="button"
            onClick={() => onPreview(publication)}
            disabled={busy}
            aria-label="Preview"
            title="Preview"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-[#E5E5E5] bg-white text-[#3C3C3C] shadow-[0_2px_0_#D0D0D0] active:translate-y-0.5 disabled:opacity-50"
          >
            <Eye className="h-4 w-4" />
          </button>
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => onToggleStar(publication)}
                disabled={busy}
                aria-pressed={isStarred}
                aria-label={isStarred ? "Unstar" : "Star (keep past 60d)"}
                title={
                  isStarred
                    ? "Unstar (allow auto-cleanup)"
                    : "Star (keep past 60d)"
                }
                className={[
                  "inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 active:translate-y-0.5 disabled:opacity-50",
                  isStarred
                    ? "border-[#F59E0B] bg-[#FEF3C7] text-[#92400E] shadow-[0_2px_0_#FDE68A]"
                    : "border-[#E5E5E5] bg-white text-[#3C3C3C] shadow-[0_2px_0_#D0D0D0]",
                ].join(" ")}
              >
                <Star
                  className={`h-4 w-4 ${isStarred ? "fill-current" : ""}`}
                />
              </button>
              <button
                type="button"
                onClick={() => onRepublish(publication)}
                disabled={busy}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border-2 border-[#6366F1] bg-[#EEF2FF] px-3 text-xs font-extrabold uppercase text-[#4338CA] shadow-[0_2px_0_#C7D2FE] active:translate-y-0.5 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
                Republish
              </button>
              <button
                type="button"
                onClick={() => onDelete(publication)}
                disabled={busy || !canManage}
                aria-label="Delete from history"
                title="Delete from history"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-[#EF4444] bg-[#FEF2F2] text-[#B91C1C] shadow-[0_2px_0_#FCA5A5] active:translate-y-0.5 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
};
